import { IChatGetHistory, IRawMessageOut } from '../types';
// import { ProviderErrorEvent } from '../models/ProviderEvents';
import { IFileResource, IMetaResource, Resource } from '../models';
import { BaseChatProvider } from './BaseChatProvider';
import { ErrorData, getPrivateKey, logError } from '../utils/logError';
import { IConnectionConfiguration } from 'src/api/configuration/types';
import { fetchGET, fetchPOST } from '@enuan/m3mgr-utils';

export class ChatHTTP
	extends BaseChatProvider
	implements IChatGetHistory<IRawMessageOut>
{
	// protected _connection?: { get: typeof fetchGET; post: typeof fetchPOST };

	protected _running: boolean = false;

	constructor(
		protected baseUrl: string,
		protected configuration: Partial<IConnectionConfiguration>,
		protected withCredentials?: boolean,
	) {
		super();
		if (!configuration.http_url)
			throw new Error('You must provide a valid HTTP url');

		// const get: typeof fetchGET = (url, getHeaders, getRequest) =>
		// 	fetchGET(new URL(url, this.baseUrl).href, undefined, (request) => {
		// 		const $request = getRequest?.(request) ?? request;
		// 		return {
		// 			...$request,
		// 			credentials: this.withCredentials ? 'include' : 'same-origin',
		// 		};
		// 	});
		// const post: typeof fetchPOST = (url, body, getHeaders, getRequest) =>
		// 	fetchPOST(new URL(url, this.baseUrl).href, body, undefined, (request) => {
		// 		const $request = getRequest?.(request) ?? request;
		// 		return {
		// 			...$request,
		// 			credentials: this.withCredentials ? 'include' : 'same-origin',
		// 		};
		// 	});
		// this._connection = { get, post };
	}

	/**
	 * Attaches the page-scoped JWT (when issued by m3-gw-chat) as a Bearer
	 * token. This keeps HTTP calls bound to this tab's tenant even when another
	 * tab replaces the ambient cookie. Servers without JWT support do not issue
	 * the field, so those clients retain the cookie-only flow.
	 */
	private withAuthHeaders = (prev: Headers) => {
		if (this.configuration.jwt) {
			prev.set('Authorization', `Bearer ${this.configuration.jwt}`);
		}
		return prev;
	};

	private get: typeof fetchGET = (url, getHeaders, getRequest) =>
		fetchGET(
			new URL(url, this.url).href,
			(prev) => this.withAuthHeaders(getHeaders ? getHeaders(prev) : prev),
			(request) => {
				const $request = getRequest?.(request) ?? request;
				return {
					...$request,
					credentials: this.withCredentials ? 'include' : 'same-origin',
				};
			},
		);
	private post: typeof fetchPOST = (url, body, getHeaders, getRequest) =>
		fetchPOST(
			new URL(url, this.url).href,
			body,
			(prev) => this.withAuthHeaders(getHeaders ? getHeaders(prev) : prev),
			(request) => {
				const $request = getRequest?.(request) ?? request;
				return {
					...$request,
					credentials: this.withCredentials ? 'include' : 'same-origin',
				};
			},
		);

	logError(id: string, data: ErrorData) {
		try {
			logError(`ChatHTTP:${id}`, data, {
				baseUrl: new URL(this.url).origin,
				privateKey: getPrivateKey(this.configuration.logger_key),
			});
		} catch (e) {
			console.error(e);
		}
	}

	/**
	 * Base URL for HTTP requests (always ends with '/' so relative paths resolve correctly).
	 * Normalizes `http_url` from config whether it has a trailing slash or not.
	 */
	get url() {
		const u = new URL(this.configuration.http_url!, this.baseUrl);
		if (!u.pathname.endsWith('/')) {
			u.pathname += '/';
		}
		return u.href;
	}

	get connection(): { get: typeof fetchGET; post: typeof fetchPOST } {
		return { get: this.get, post: this.post };
	}

	sendFile(file: File, _count?: number): Promise<IMetaResource> {
		const count = _count ?? this.configuration.reconnection_retries ?? 5;
		const formData = new FormData();
		formData.append('file', file);

		return this.post<IMetaResource>('files', formData).catch((error) => {
			this.logError('sendFile', { error, payload: { file } });
			if (count > 0) {
				return new Promise<IMetaResource>((resolve) => {
					setTimeout(() => {
						resolve(this.sendFile(file, count - 1));
					}, this.configuration.reconnection_timeout ?? 10000);
				});
			} else {
				throw new Error('sendFile - Max retry');
			}
		});
	}
	getFile(file: IFileResource) {
		return fetch(file.data)
			.then((res) => res.blob())
			.then(
				(blob) =>
					new File([blob], file.name, {
						type: file.mime_type,
					}),
			);
	}
	sendFileMessage(file: IFileResource) {
		return this.getFile(file).then(this.sendFile.bind(this)).then(Resource);
	}

	getHistory(timestamp?: string, limit?: number): Promise<IRawMessageOut[]> {
		const params = new URLSearchParams();
		if (timestamp) params.append('timestamp', timestamp);
		if (limit) params.append('limit', limit.toString());
		return this.get<IRawMessageOut[]>(`messages/history?${params.toString()}`);
	}

	close() {
		const url = new URL('./reset', this.url);
		return fetch(url, { method: 'POST' }).then((response) => {
			if (!response.ok) {
				throw new Error(`Failed to reset connection: ${response.statusText}`);
			}
			return response;
		});
	}
}
